import { StateCreator } from "zustand"
import { NexPageSize, nexDefaultPagination } from "../components/Table/pageable-table/NexPageSize"
import { DataReceiptTableFilters } from "../models/DataReceiptQueries"
import { Order } from "../models/SortOrder"
import { OfficeId } from "../store/types/MissionsTypes"
import { TimeUtilsDateFns } from "../utils/TimeUtils"

export enum SelectMissionsOrder {
    FLOWN_DATE = "mission__flown_date",
}

export interface DataReceiptTableState {
    tableIsLoading: boolean

    // Table Paging
    page: number
    pageSize: NexPageSize
    // Table Sorting
    order: Order
    orderBy: SelectMissionsOrder

    // Table Filtering
    filters: DataReceiptTableFilters
}

interface DataReceiptTableActions {
    setTableIsLoading: (bool: boolean) => void

    // Table Paging Actions
    setPage: (page: number) => void
    setPageSize: (pageSize: NexPageSize) => void

    // Table Sorting Actions
    setOrder: (order: Order) => void
    setOrderBy: (orderBy: SelectMissionsOrder) => void

    setFilter: <T extends keyof DataReceiptTableFilters>(field: T, value: DataReceiptTableFilters[T]) => void
    clearFilters: () => void
}

export const initTableFilters: () => DataReceiptTableFilters = () => ({
    mission: "",
    project: "",
    source: "",
    tracking_number: "",
    office: [],
    mission_flown_date: TimeUtilsDateFns.getFormattedNumberOfDaysAgo(60),
})

export const initDataReceiptTableState: () => DataReceiptTableState = () => ({
    tableIsLoading: false,

    // Table Paging
    page: nexDefaultPagination.page,
    pageSize: nexDefaultPagination.pageSize,

    // Table Sort
    order: Order.desc,
    orderBy: SelectMissionsOrder.FLOWN_DATE,

    // Default Filters
    filters: initTableFilters(),
})

export type DataReceiptTableStoreType = DataReceiptTableState & DataReceiptTableActions

export const createDataReceiptTableSlice: StateCreator<DataReceiptTableStoreType, [], [], DataReceiptTableStoreType> = (
    set,
) => ({
    ...initDataReceiptTableState(),
    setTableIsLoading: (bool) => set({ tableIsLoading: bool }),
    setPage: (page) => set({ page }),
    setPageSize: (pageSize) => set({ pageSize }),
    setOrder: (order) => set({ order }),
    setOrderBy: (orderBy) => set({ orderBy }),
    setFilter: (field, value) => {
        switch (field) {
            case "mission":
            case "project":
            case "source":
            case "tracking_number":
            case "mission_flown_date":
                if (typeof value === "string") {
                    set((state) => ({ filters: { ...state.filters, [field]: value }, page: 0 }))
                }
                break
            case "office": {
                let officeValue: OfficeId[] = []
                if (Array.isArray(value)) {
                    officeValue = value.filter((val) => val in OfficeId)
                } else {
                    officeValue = []
                }

                set((state) => ({ filters: { ...state.filters, [field]: officeValue }, page: 0 }))

                break
            }
            default:
                throw new Error(`Field ${field} not supported to set as a filter with value ${value}`)
        }
    },
    clearFilters: () => set({ filters: initTableFilters() }),
})
